import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../context/AuthContext';

const API_BASE = process.env.REACT_APP_API_URL || 'http://168.231.118.116:3001';
const ZEE_EMAILS = ['chris@rainbow-sails.com', 'zee@rainbow-sails.com'];
const POLL_INTERVAL = 5000;

const SubscriberChat = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [allowed, setAllowed] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);

  const pollRef = useRef(null);
  const lastTimestampRef = useRef(null);
  const listRef = useRef(null);

  const isZee = user?.email ? ZEE_EMAILS.includes(user.email.toLowerCase()) : false;

  const scrollToBottom = useCallback(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, []);

  const mergeMessages = useCallback((newMessages) => {
    if (!Array.isArray(newMessages) || newMessages.length === 0) {
      return;
    }

    setMessages((prev) => {
      const map = new Map(prev.map((m) => [m.id, m]));
      newMessages.forEach((message) => {
        map.set(message.id, message);
      });
      return Array.from(map.values()).sort((a, b) => a.timestamp - b.timestamp);
    });

    lastTimestampRef.current = newMessages[newMessages.length - 1].timestamp;
  }, []);

  const fetchMessages = useCallback(
    async (initial = false) => {
      if (!user) return;
      try {
        const params = new URLSearchParams({
          userId: user.id,
          email: user.email,
        });

        if (!initial && lastTimestampRef.current) {
          params.append('since', lastTimestampRef.current);
        }

        const response = await fetch(`${API_BASE}/api/subscriber-chat/messages?${params.toString()}`);
        const data = await response.json();
        if (!response.ok || !data.success) {
          throw new Error(data.error || 'Unable to load messages');
        }

        mergeMessages(data.messages || []);
        setAllowed(true);
        setError(null);
        if (initial) {
          setTimeout(scrollToBottom, 100);
        }
      } catch (err) {
        console.error('Failed to load subscriber chat messages:', err);
        setError(err.message);
        if (initial) {
          setAllowed(false);
        }
      } finally {
        if (initial) {
          setLoading(false);
        }
      }
    },
    [user, mergeMessages, scrollToBottom]
  );

  useEffect(() => {
    if (!user) {
      setAllowed(false);
      setMessages([]);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    setMessages([]);
    lastTimestampRef.current = null;

    const initialise = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/subscriptions/${user.id}`);
        const data = await response.json();
        const hasActiveSubscription = data?.subscription?.status === 'active';
        const canAccess = hasActiveSubscription || isZee;

        if (!canAccess) {
          setAllowed(false);
          setLoading(false);
          setError('Zee needs an active mystery subscription test to enter this chat.');
          return;
        }

        await fetchMessages(true);

        if (pollRef.current) {
          clearInterval(pollRef.current);
        }
        pollRef.current = setInterval(() => fetchMessages(false), POLL_INTERVAL);
      } catch (err) {
        console.error('Failed to verify subscription for chat access:', err);
        setAllowed(false);
        setError('Unable to verify Zee mystery subscription test access right now.');
      } finally {
        setLoading(false);
      }
    };

    initialise();

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
      }
    };
  }, [user, isZee, fetchMessages]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const handleSend = async () => {
    if (!user || sending) return;
    const trimmed = input.trim();
    if (!trimmed) return;

    setSending(true);
    try {
      const response = await fetch(`${API_BASE}/api/subscriber-chat/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.id,
          email: user.email,
          displayName: user.firstName ? `${user.firstName} ${user.lastName || ''}`.trim() : user.email.split('@')[0],
          message: trimmed,
        }),
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to send message');
      }

      mergeMessages([data.message]);
      setInput('');
      setError(null);
      setTimeout(scrollToBottom, 50);
    } catch (err) {
      console.error('Failed to send chat message:', err);
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  const formattedMessages = useMemo(
    () => messages.map((message) => ({
      ...message,
      formattedTime: new Date(message.timestamp).toLocaleString('en-AU', {
        hour: '2-digit',
        minute: '2-digit',
        day: 'numeric',
        month: 'short',
      }),
    })),
    [messages]
  );

  if (!user) {
    return (
      <div style={styles.container}>
        <p style={styles.infoText}>
          Zee\'s mystery chat unlocks once you sign in and support the experiment.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={styles.container}>
        <p style={styles.infoText}>Loading Zee chat...</p>
      </div>
    );
  }

  if (!allowed) {
    return (
      <div style={styles.container}>
        <p style={styles.infoText}>
          {error || 'Only active mystery subscription test supporters (and Zee) can join this chat.'}
        </p>
      </div>
    );
  }

  return (
    <div style={styles.chatWrapper}>
      <div style={styles.header}>
        <div>
          <h3 style={styles.title}>Zee\'s Mystery Subscription Test Lounge</h3>
          <p style={styles.subtitle}>First dibs, quiet whispers, and Zee\'s secret drops.</p>
        </div>
        <span style={styles.badge}>{isZee ? 'Zee' : 'Subscriber'}</span>
      </div>

      <div style={styles.messages} ref={listRef}>
        {formattedMessages.length === 0 ? (
          <div style={styles.emptyState}>
            <p style={{ margin: 0, color: '#c4b5fd' }}>
              Zee hasn\'t dropped a message yet. Say hello and claim your spot!
            </p>
          </div>
        ) : (
          formattedMessages.map((message) => (
            <div
              key={message.id}
              style={{
                ...styles.message,
                alignSelf: message.email === user.email ? 'flex-end' : 'flex-start',
                background: message.role === 'creator'
                  ? 'linear-gradient(135deg, rgba(236, 72, 153, 0.35), rgba(139, 92, 246, 0.35))'
                  : 'rgba(15, 23, 42, 0.65)',
                border: message.email === user.email ? '1px solid rgba(236, 72, 153, 0.5)' : '1px solid rgba(148, 163, 184, 0.25)',
              }}
            >
              <div style={styles.messageHeader}>
                <span style={styles.messageAuthor}>
                  {message.displayName || 'Mystery Subscription Tester'}
                  {message.role === 'creator' ? ' • Zee' : ''}
                </span>
                <span style={styles.messageTime}>{message.formattedTime}</span>
              </div>
              <p style={styles.messageContent}>{message.content}</p>
            </div>
          ))
        )}
      </div>

      {error && (
        <div style={styles.error}>
          {error}
        </div>
      )}

      <div style={styles.inputRow}>
        <textarea
          value={input}
          onChange={(event) => setInput(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Zee\'s whisper: drop your next idea or question..."
          style={styles.textarea}
          maxLength={1000}
        />
        <button
          onClick={handleSend}
          disabled={sending || input.trim().length === 0}
          style={{
            ...styles.sendButton,
            opacity: sending || input.trim().length === 0 ? 0.6 : 1,
            cursor: sending || input.trim().length === 0 ? 'not-allowed' : 'pointer',
          }}
        >
          {sending ? 'Sending...' : 'Send to Zee'}
        </button>
      </div>
    </div>
  );
};

const styles = {
  container: {
    background: 'rgba(15, 23, 42, 0.6)',
    border: '1px solid rgba(148, 163, 184, 0.25)',
    borderRadius: '16px',
    padding: '24px',
    color: '#e2e8f0',
    textAlign: 'center',
  },
  chatWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    background: 'linear-gradient(160deg, rgba(17, 24, 39, 0.92) 0%, rgba(30, 41, 59, 0.85) 45%, rgba(59, 7, 100, 0.9) 100%)',
    border: '1px solid rgba(129, 140, 248, 0.35)',
    borderRadius: '18px',
    padding: '20px',
    boxShadow: '0 25px 45px rgba(15, 23, 42, 0.45)',
    color: '#f8fafc',
    minHeight: '420px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '12px',
    borderBottom: '1px solid rgba(148, 163, 184, 0.25)',
    paddingBottom: '12px',
  },
  title: {
    margin: 0,
    fontSize: '20px',
    fontWeight: 700,
    letterSpacing: '0.02em',
  },
  subtitle: {
    margin: '4px 0 0 0',
    fontSize: '14px',
    color: '#c4b5fd',
  },
  badge: {
    background: 'rgba(236, 72, 153, 0.2)',
    color: '#f9a8d4',
    border: '1px solid rgba(236, 72, 153, 0.4)',
    padding: '6px 14px',
    borderRadius: '999px',
    fontSize: '12px',
    fontWeight: 600,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
  },
  messages: {
    flex: 1,
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    padding: '4px',
  },
  message: {
    maxWidth: '75%',
    padding: '12px 16px',
    borderRadius: '14px',
    backdropFilter: 'blur(6px)',
  },
  messageHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
  },
  messageAuthor: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#e9d5ff',
  },
  messageTime: {
    fontSize: '12px',
    color: '#cbd5f5',
  },
  messageContent: {
    margin: 0,
    color: '#f1f5f9',
    lineHeight: 1.5,
    whiteSpace: 'pre-wrap',
  },
  emptyState: {
    textAlign: 'center',
    padding: '40px 12px',
    borderRadius: '12px',
    background: 'rgba(30, 64, 175, 0.15)',
    border: '1px dashed rgba(129, 140, 248, 0.4)',
  },
  error: {
    background: 'rgba(239, 68, 68, 0.15)',
    border: '1px solid rgba(248, 113, 113, 0.4)',
    borderRadius: '10px',
    padding: '10px 12px',
    color: '#fecaca',
    fontSize: '13px',
  },
  inputRow: {
    display: 'flex',
    gap: '12px',
    alignItems: 'flex-end',
  },
  textarea: {
    flex: 1,
    minHeight: '68px',
    maxHeight: '160px',
    background: 'rgba(15, 23, 42, 0.85)',
    border: '1px solid rgba(148, 163, 184, 0.35)',
    borderRadius: '12px',
    padding: '12px',
    color: '#f8fafc',
    fontSize: '14px',
    lineHeight: 1.5,
    fontFamily: 'inherit',
    resize: 'vertical',
  },
  sendButton: {
    background: 'linear-gradient(135deg, #ec4899, #8b5cf6)',
    border: 'none',
    borderRadius: '12px',
    color: '#ffffff',
    fontSize: '14px',
    fontWeight: 600,
    padding: '12px 20px',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  },
  infoText: {
    margin: 0,
    fontSize: '14px',
    color: '#cbd5f5',
  },
};

export default SubscriberChat;

